"use client";
import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Count } from "../atoms/count";
import { isDefined } from "@centisystems/lib/utils";
import clsx from "clsx";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

type TabItem = {
  name: string;
  route: string;
  total?: number;
  showUnderline?: boolean;
};

export function TabCount({
  tabs,
  className,
}: {
  tabs: TabItem[];
  className?: string;
}) {
  const path = usePathname();
  const listRef = React.useRef<HTMLUListElement>(null);
  const hoverScrollRef = React.useRef<number | null>(null);
  const hoverDirectionRef = React.useRef<"left" | "right" | null>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const list = listRef.current;

    if (!list) return;

    const maxScrollLeft = list.scrollWidth - list.clientWidth;
    setCanScrollLeft(list.scrollLeft > 8);
    setCanScrollRight(list.scrollLeft < maxScrollLeft - 8);
  }, []);

  const stopHoverScroll = React.useCallback(() => {
    if (hoverScrollRef.current !== null) {
      window.clearInterval(hoverScrollRef.current);
      hoverScrollRef.current = null;
    }
    hoverDirectionRef.current = null;
  }, []);

  const scrollTabs = React.useCallback((direction: "left" | "right") => {
    const list = listRef.current;

    if (!list) return;

    const distance = Math.max(list.clientWidth * 0.65, 120);
    list.scrollBy({
      left: direction === "left" ? -distance : distance,
      behavior: "smooth",
    });
  }, []);

  const startHoverScroll = React.useCallback(
    (direction: "left" | "right") => {
      stopHoverScroll();
      hoverDirectionRef.current = direction;

      hoverScrollRef.current = window.setInterval(() => {
        const list = listRef.current;

        if (!list) {
          stopHoverScroll();
          return;
        }

        const maxScrollLeft = list.scrollWidth - list.clientWidth;
        const isAtLeftEdge = list.scrollLeft <= 0;
        const isAtRightEdge = list.scrollLeft >= maxScrollLeft;

        if (
          (direction === "left" && isAtLeftEdge) ||
          (direction === "right" && isAtRightEdge)
        ) {
          stopHoverScroll();
          return;
        }

        list.scrollBy({
          left: direction === "left" ? -18 : 18,
          behavior: "auto",
        });
      }, 16);
    },
    [stopHoverScroll],
  );

  React.useEffect(() => {
    if (
      (hoverDirectionRef.current === "left" && !canScrollLeft) ||
      (hoverDirectionRef.current === "right" && !canScrollRight)
    ) {
      stopHoverScroll();
    }
  }, [canScrollLeft, canScrollRight, stopHoverScroll]);

  React.useEffect(() => {
    updateScrollState();

    const list = listRef.current;

    if (!list) return;

    const activeTab = list.querySelector("[data-active='true']");

    if (activeTab instanceof HTMLElement) {
      activeTab.scrollIntoView({
        inline: "center",
        block: "nearest",
        behavior: "smooth",
      });
    }

    list.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      list.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      stopHoverScroll();
    };
  }, [path, tabs, stopHoverScroll, updateScrollState]);

  return (
    <div className="relative">
      <div
        className={clsx(
          "pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-white via-white/95 to-transparent transition-opacity md:hidden",
          canScrollLeft ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        className={clsx(
          "pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-white via-white/95 to-transparent transition-opacity md:hidden",
          canScrollRight ? "opacity-100" : "opacity-0",
        )}
      />
      {canScrollLeft && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          className="absolute left-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-white/95 text-zinc-700 shadow-sm backdrop-blur transition hover:bg-white md:hidden"
          onClick={() => scrollTabs("left")}
          onMouseEnter={() => startHoverScroll("left")}
          onMouseLeave={stopHoverScroll}
          onTouchStart={stopHoverScroll}>
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
      )}
      {canScrollRight && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          className="absolute right-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-zinc-200 bg-white/95 text-zinc-700 shadow-sm backdrop-blur transition hover:bg-white md:hidden"
          onClick={() => scrollTabs("right")}
          onMouseEnter={() => startHoverScroll("right")}
          onMouseLeave={stopHoverScroll}
          onTouchStart={stopHoverScroll}>
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      )}
      <ul
        ref={listRef}
        role="list"
        className={clsx(
          "scrollbar-hide flex w-full md:w-auto snap-x snap-mandatory overflow-x-auto scroll-smooth flex-none gap-x-4 text-sm lg:text-[15px] font-medium leading-6 text-gray-400 z-30 pr-8 md:pr-4",
          className,
        )}>
        {tabs.map((item) => {
          const isActive = path === item.route;
          return (
            <li
              key={item.name}
              className="relative flex snap-start items-center shrink-0">
              <Link
                href={item.route}
                data-active={isActive}
                className={clsx(
                  "text-nowrap rounded-full px-0 py-2 transition-colors",
                  isActive
                    ? "font-semibold text-black"
                    : "text-gray-500 hover:text-zinc-800",
                )}>
                {item.name}
              </Link>
              {isDefined(item.total) && (
                <Count
                  className="mb-0"
                  count={item.total ?? 0}
                  active={isActive}
                />
              )}
              {item.showUnderline !== false && isActive && (
                <span className="absolute bottom-0 left-0 h-[2px] w-full rounded-full bg-black" />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
